import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { ActiveUserStore } from '../../stores/user/active-user.state';
import { filter, debounceTime } from 'rxjs/operators';
import { Tracking } from '../../models/tracking';
import { environment } from '../../../environments/environment';

// Regex patterns for route classification
const ROUTE_ANSICHT = /\/ansicht\/([a-f0-9]{24})/i;
const ROUTE_ATLAS_EXTERNAL = /^\/atlas\/[^/]+/;
const ROUTE_ATLAS_INTERNAL = /^\/atlas-intern/;
const ROUTE_DATA_BACKEND = /^\/data-backend/;

// Kill switch for the tracking feature. Tracking (and the two-tier consent
// dialog it needs) must stay disabled until it's cleared for the deploy —
// flip this back to true to re-enable both.
export const TRACKING_ENABLED = false;

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private readonly CONSENT_COOKIE = 'hyperion-tracking-consent';
  private readonly VISITOR_ID_KEY = 'hyperion-visitor-id';
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly userStore = inject(ActiveUserStore);

  constructor() {
    if (TRACKING_ENABLED) {
      this.initRouteChangeTracking();
    }
  }

  hasTrackingConsent(): boolean {
    return (
      document.cookie.includes(`${this.CONSENT_COOKIE}=true`) ||
      localStorage.getItem(this.CONSENT_COOKIE) === 'true'
    );
  }

  setConsent(consented: boolean): void {
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);

    if (consented) {
      document.cookie = `${this.CONSENT_COOKIE}=true; expires=${expiryDate.toUTCString()}; path=/`;
      localStorage.setItem(this.CONSENT_COOKIE, 'true');
    } else {
      document.cookie = `${this.CONSENT_COOKIE}=false; expires=${expiryDate.toUTCString()}; path=/`;
      localStorage.setItem(this.CONSENT_COOKIE, 'false');
    }
  }

  private initRouteChangeTracking(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(500)
      )
      .subscribe(event => {
        const url = event.urlAfterRedirects;
        const isExternal = ROUTE_ATLAS_EXTERNAL.test(url);

        // Public (external) atlas routes: track without consent requirement —
        // communeId is resolved via the routeContextGuard before NavigationEnd fires.
        if (isExternal) {
          this.trackRouteChange(url, 'external', true);
          return;
        }

        // Internal routes: respect the user's tracking consent
        if (this.hasTrackingConsent()) {
          const context = ROUTE_ATLAS_INTERNAL.test(url) ? 'external' : 'internal';
          this.trackRouteChange(url, context, false);
        }
      });
  }

  // Random, non-personally-identifying id persisted in this browser so that
  // anonymous (not-logged-in) page views can be grouped into a session —
  // e.g. for dwell-time analysis of public atlas traffic. Never derived
  // from IP or any other identifying data.
  private getOrCreateVisitorId(): string {
    let id = localStorage.getItem(this.VISITOR_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(this.VISITOR_ID_KEY, id);
    }
    return id;
  }

  private trackRouteChange(url: string, context: 'internal' | 'external', isPublic: boolean): void {
    const communeId = this.userStore.commune();
    if (!communeId) return;

    const userId = isPublic
      ? this.getOrCreateVisitorId()
      : this.userStore.user()?._id || null;

    const details: Tracking['details'] = { url };

    // Extract bookmarkId when on an Ansichten-route
    const ansichtMatch = ROUTE_ANSICHT.exec(url);
    if (ansichtMatch) {
      details['bookmarkId'] = ansichtMatch[1];
    }

    const trackingEvent: Partial<Tracking> = {
      communeId,
      userId,
      type: 'ROUTE_CHANGE',
      action: 'VIEW',
      context,
      isPublic,
      details,
    };

    this.sendTrackingEvent(trackingEvent);
  }

  public trackCRUD(action: string, targetCollection: string, details: any): void {
    if (!TRACKING_ENABLED || !this.hasTrackingConsent()) return;

    const communeId = this.userStore.commune();
    const userId = this.userStore.user()?._id || null;

    if (!communeId) return;

    const trackingEvent: Partial<Tracking> = {
      communeId,
      userId,
      type: 'CRUD',
      action,
      targetCollection,
      context: 'internal',
      isPublic: false,
      details,
    };

    this.sendTrackingEvent(trackingEvent);
  }

  private sendTrackingEvent(event: Partial<Tracking>): void {
    const url = `${environment.apiUrl}/api/tracking/insert`;
    this.http.post(url, event).subscribe({
      error: err => console.error('Failed to log tracking event', err),
    });
  }
}
